import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FilePickerComponent } from '@components/file-picker/file-picker.component';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { CommonUtilityHelper } from '@services/common-utility-helper/common-utility-helper';
import { ConnectionService } from '@services/connection-service';
import { DataStoreService } from '@services/data-store-service';
import { EventLoggerService } from '@services/event-logger-service';
import { UserService } from '@services/user';

import { AppConfig } from '../../../app.config';
import { ReportUploadSheetComponent, ReportUploadSheetType, REPORT_UPLOAD_SHEET_TYPE } from './report-upload-sheet/report-upload-sheet.component';

const REPORT_CLASSIFY_ERROR_TYPE = 'INVALID_DATA';

interface CardData {
  type: string;
  param: string;
  url: {
    female: string;
    male: string;
  };
}

interface GenderCardData {
  type: string;
  param: string;
  url: string;
}
@Component({
  selector: 'app-select-checkup-type',
  templateUrl: './select-checkup-type.component.html',
  styles: [':host {@apply tw-block tw-h-full tw-overflow-y-scroll; }'],
  standalone: false,
})

export class SelectCheckupTypeComponent implements OnInit, OnDestroy {
  isInternalUser: boolean;
  isPaidUser: boolean = false;
  uploadFileExp: boolean = true;
  hasBodyRegimen: boolean = false;

  @ViewChildren(FilePickerComponent) filePickerComponent: QueryList<FilePickerComponent>;

  user: any;
  experiments: any;
  displayCards: any;
  maleCardsData: Array<object>;
  femaleCardsData: Array<object>;
  userLanguage: string;
  private subscriptions: Subscription[] = [];

  cardsData: Array<CardData> = [
    {
      type: this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE,
      param: this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE,
      url: {
        female: 'https://cdn.cureskin.com/app/img/female-front-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-front-face-rebranded.png',
      },
    },
    {
      type: this.appConfig.Shared.InstantCheckup.Type.RIGHT_FACE,
      param: this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE,
      url: {
        female: 'https://cdn.cureskin.com/app/img/female-right-side-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-right-face-rebranded.png',
      },
    },
    {
      type: this.appConfig.Shared.InstantCheckup.Type.LEFT_FACE,
      param: this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE,
      url: {
        female: 'https://cdn.cureskin.com/app/img/female-left-side-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-left-face_rebranded.png',
      },
    },
    {
      type: this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
      param: this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
      url: {
        female: 'https://cdn.cureskin.com/app/img/female-front-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-front-face-rebranded.png',
      },
    },
    {
      type: this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
      param: this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
      url: {
        female: 'https://cdn.cureskin.com/app/img/female-hair-top-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-hair-top-rebranded.png',
      },
    },
  ];

  constructor(private conn: ConnectionService,
    private router: Router,
    public appConfig: AppConfig,
    private eventLogger: EventLoggerService,
    private broadcast: BroadcastService,
    private commonUtilityHelper: CommonUtilityHelper,
    private dataStore: DataStoreService,
    private appWebBridgeService: AppWebBridgeService,
    private bottomSheet: MatBottomSheet,
    private userService: UserService) {
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridgeService.setStatusBarColor('cureBlack');
    this.user = await this.conn.getActingUser();
    this.setDisplayCards();
    this.isInternalUser = this.conn.isInternalUser();
    this.isPaidUser = this.userService.isPaidUser(this.user);
    this.userLanguage = this.user.get('languagePreference');
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'instant-checkup' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    const allRegimens = await this.conn.fetchRegimens(null, false);
    const regimens = allRegimens.filter((each): boolean => each.active);
    this.hasBodyRegimen = regimens.some((each): boolean => each.class === this.appConfig.Shared.Regimen.Class.BODY);
    this.subscriptions.push(
      this.broadcast.on<{ name: string; type: string; size: number; data: string }>('FILE_UPLOAD_DATA')
        .subscribe((payload) => this.handleNativeFilePicked(payload)),
      this.broadcast.on<{ error: string }>('FILE_UPLOAD_ERROR')
        .subscribe((payload) => this.broadcast.broadcast('NOTIFY', { message: payload?.error || 'Failed to read file' })),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  setDisplayCards(): void {
    this.femaleCardsData = this.cardsData.map((item: CardData): GenderCardData => ({ ...item, url: item.url.female }));
    this.maleCardsData = this.cardsData.map((item: CardData): GenderCardData => ({ ...item, url: item.url.male }));
    this.displayCards = this.user?.get('Gender') === this.appConfig.Shared.Gender.FEMALE ? this.femaleCardsData : this.maleCardsData;
  }

  showPreview(tag: string): void {
    if (this.uploadFileExp && [this.appConfig.Shared.InstantCheckup.Type.REPORT].includes(tag)) {
      this.openFilePicker(this.appConfig.Shared.InstantCheckup.Type.REPORT);
      return;
    }
    if (tag === this.appConfig.Shared.InstantCheckup.Type.REPORT) return;
    this.router.navigate(['/user/instantCheckup/preview'], { queryParams: { tag } });
  }

  viewUploadedPhotos(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'view-uploaded-photos' }));
    this.router.navigate(['/user/instantCheckup/list']);
  }

  viewUploadedDocuments(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'view-uploaded-documents' }));
    this.router.navigate(['/user/instantCheckup/list'], { queryParams: { mode: 'documents' } });
  }

  async onFileChange(event: any): Promise<void> {
    const file = event.target.files[0];
    if (!file) return;
    if (!this.commonUtilityHelper.isFileSizeLesserThanMaxSize(file.size, 5)) {
      this.broadcast.broadcast('NOTIFY', { message: 'File size should be less than 5MB. Kindly retry.' });
      return;
    }
    await this.processReportUpload(file);
  }

  async uploadImageFromNativeCamera(event: any): Promise<any> {
    const nativeCameraImage = event.target.files[0];
    this.dataStore.set('IMAGE_FILE', { file: nativeCameraImage });
    await this.router.navigate(['/user/instantCheckup/capture'],
      { queryParams: { tag: this.appConfig.Shared.InstantCheckup.Type.REPORT, nativeCameraFile: true }, replaceUrl: true });
  }

  async handleNativeFilePicked(payload: { name: string; type: string; size: number; data: string }): Promise<void> {
    if (!this.commonUtilityHelper.isFileSizeLesserThanMaxSize(payload.size, 5)) {
      this.broadcast.broadcast('NOTIFY', { message: 'File size should be less than 5MB. Kindly retry.' });
      return;
    }
    const byteString = atob(payload.data);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }
    const file = new File([byteArray], payload.name, { type: payload.type });
    await this.processReportUpload(file);
  }

  openFilePicker(type: string): void {
    if (type === this.appConfig.Shared.InstantCheckup.Type.REPORT) {
      if (this.appWebBridgeService.isAppWebBridgeLoaded()) {
        this.appWebBridgeService.openFilePicker();
      } else {
        this.filePickerComponent.last.openFileIntent();
      }
      return;
    }
    this.filePickerComponent.first.openFileIntent();
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  private openReportSheet(type: ReportUploadSheetType): void {
    this.bottomSheet.open(ReportUploadSheetComponent, { data: { type } });
  }

  private async processReportUpload(file: File): Promise<void> {
    let uploadResponse: { publicURL: string; signedGetURL: string };
    try {
      uploadResponse = await this.conn.uploadFile({
        file,
        bucket: 'OTHER',
        username: this.user?.get('username'),
        source: 'doctor_other',
      });
    } catch {
      this.broadcast.broadcast('NOTIFY', { message: 'Failed to upload file. Please try again.' });
      return;
    }

    let classification: { isReport?: boolean };
    try {
      classification = await this.conn.classifyReport(uploadResponse.publicURL);
    } catch (error: any) {
      const errorData = error?.response?.data || error?.error || error;
      if (errorData?.type === REPORT_CLASSIFY_ERROR_TYPE) {
        this.openReportSheet(REPORT_UPLOAD_SHEET_TYPE.ERROR);
      } else {
        this.broadcast.broadcast('NOTIFY', { message: 'Something went wrong. Please try again.' });
      }
      return;
    }

    this.openReportSheet(classification.isReport ? REPORT_UPLOAD_SHEET_TYPE.SUCCESS : REPORT_UPLOAD_SHEET_TYPE.NOT_REPORT);
  }
}
